import { useState } from 'react';
import { useMutation } from '@apollo/client';
import {
  Plus,
  Mail,
  Phone,
  Pencil,
  Trash2,
  User,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { cn } from '@/lib/utils';
import { validateEmail, validatePhone, formatPhoneNumber } from '@/lib/validations';
import {
  CREATE_CONTACT,
  UPDATE_CONTACT,
  DELETE_CONTACT,
  GET_ORGANIZATION,
  type Contact,
  type CreateContactInput,
  type UpdateContactInput,
} from '@/graphql/organizations';

type ContactType = 'PRIMARY' | 'BILLING' | 'EXECUTIVE' | 'TECHNICAL' | 'SUPPORT' | 'DECISION_MAKER';

const CONTACT_TYPE_LABELS: Record<ContactType, string> = {
  PRIMARY: 'Primary',
  BILLING: 'Billing',
  EXECUTIVE: 'Executive',
  TECHNICAL: 'Technical',
  SUPPORT: 'Support',
  DECISION_MAKER: 'Decision Maker',
};

const CONTACT_TYPE_STYLES: Record<ContactType, string> = {
  PRIMARY: 'badge-primary',
  BILLING: 'bg-turquoise/10 text-turquoise dark:bg-aqua/20 dark:text-aqua',
  EXECUTIVE: 'bg-violet/10 text-violet dark:bg-violet-light/20 dark:text-violet-light',
  TECHNICAL: 'bg-orange/10 text-orange dark:bg-orange/20 dark:text-orange-light',
  SUPPORT: 'badge-neutral',
  DECISION_MAKER: 'bg-yellow/10 text-yellow-700 dark:bg-yellow/20 dark:text-yellow-400',
};

interface ContactsTabProps {
  clientId: string;
  contacts?: Contact[];
  onRefresh?: () => void;
}

export function ContactsTab({ clientId, contacts = [], onRefresh }: ContactsTabProps) {
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  
  // Primary contact confirmation state
  const [showPrimaryConfirm, setShowPrimaryConfirm] = useState(false);
  const [currentPrimaryContact, setCurrentPrimaryContact] = useState<Contact | null>(null);

  // Form state
  const [formFirstName, setFormFirstName] = useState('');
  const [formLastName, setFormLastName] = useState('');
  const [formTitle, setFormTitle] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formPhone, setFormPhone] = useState('');
  const [formTypes, setFormTypes] = useState<ContactType[]>(['PRIMARY']);

  // Validation error state
  const [emailError, setEmailError] = useState<string | undefined>();
  const [phoneError, setPhoneError] = useState<string | undefined>();
  const [touched, setTouched] = useState({ email: false, phone: false });

  // GraphQL mutations
  const [createContact, { loading: creating }] = useMutation(CREATE_CONTACT, {
    refetchQueries: [{ query: GET_ORGANIZATION, variables: { id: clientId } }],
    onCompleted: () => {
      setShowAddModal(false);
      resetForm();
      onRefresh?.();
    },
  });

  const [updateContact, { loading: updating }] = useMutation(UPDATE_CONTACT, {
    refetchQueries: [{ query: GET_ORGANIZATION, variables: { id: clientId } }],
    onCompleted: () => {
      setEditingContact(null);
      resetForm();
      onRefresh?.();
    },
  });

  const [deleteContact, { loading: deleting }] = useMutation(DELETE_CONTACT, {
    refetchQueries: [{ query: GET_ORGANIZATION, variables: { id: clientId } }],
    onCompleted: () => {
      setShowDeleteConfirm(null);
      onRefresh?.();
    },
  });

  const resetForm = () => {
    setFormFirstName('');
    setFormLastName('');
    setFormTitle('');
    setFormEmail('');
    setFormPhone('');
    setFormTypes(['PRIMARY']);
    setEmailError(undefined);
    setPhoneError(undefined);
    setTouched({ email: false, phone: false });
  };

  const handleTypeToggle = (type: ContactType) => {
    setFormTypes((prev) => {
      if (prev.includes(type)) {
        // Don't allow removing all types - at least one required
        if (prev.length === 1) return prev;
        return prev.filter((t) => t !== type);
      }
      return [...prev, type];
    });
  };

  // Validation handlers
  const handleEmailChange = (value: string) => {
    setFormEmail(value);
    // Clear error when user starts typing if field was touched
    if (touched.email) {
      setEmailError(validateEmail(value));
    }
  };

  const handleEmailBlur = () => {
    setTouched((prev) => ({ ...prev, email: true }));
    setEmailError(validateEmail(formEmail));
  };

  const handlePhoneChange = (value: string) => {
    // Auto-format phone number as user types
    const formatted = formatPhoneNumber(value);
    setFormPhone(formatted);
    // Clear error when user starts typing if field was touched
    if (touched.phone && formatted) {
      setPhoneError(validatePhone(formatted));
    } else if (!formatted) {
      setPhoneError(undefined); // Phone is optional
    }
  };

  const handlePhoneBlur = () => {
    setTouched((prev) => ({ ...prev, phone: true }));
    if (formPhone) {
      setPhoneError(validatePhone(formPhone));
    }
  };

  // Check if form is valid for submission
  const isFormValid = () => {
    const emailErr = validateEmail(formEmail);
    const phoneErr = validatePhone(formPhone);
    return !emailErr && !phoneErr && formFirstName && formLastName;
  };

  const openAddModal = () => {
    resetForm();
    setShowAddModal(true);
  };

  const openEditModal = (contact: Contact) => {
    setFormFirstName(contact.firstName);
    setFormLastName(contact.lastName);
    setFormTitle(contact.title || '');
    setFormEmail(contact.email);
    setFormPhone(contact.phone || '');
    // Use types array if available, fallback to single type
    setFormTypes(contact.types || [contact.type]);
    setEditingContact(contact);
  };

  // Find the current primary contact (if any, excluding the contact being edited)
  const findExistingPrimaryContact = (): Contact | null => {
    return contacts.find((c) => {
      // Exclude the contact being edited
      if (editingContact && c.id === editingContact.id) return false;
      // Check if contact has PRIMARY in their types
      const types = c.types || [c.type];
      return types.includes('PRIMARY');
    }) || null;
  };

  // Check if PRIMARY is being added (wasn't in original types)
  const isAddingPrimary = (): boolean => {
    if (!formTypes.includes('PRIMARY')) return false;
    if (editingContact) {
      const originalTypes = editingContact.types || [editingContact.type];
      return !originalTypes.includes('PRIMARY');
    }
    // For new contacts, any PRIMARY selection is "adding"
    return true;
  };

  const handleSaveClick = () => {
    // Check if we're adding PRIMARY and there's already a primary contact
    if (isAddingPrimary()) {
      const existingPrimary = findExistingPrimaryContact();
      if (existingPrimary) {
        setCurrentPrimaryContact(existingPrimary);
        setShowPrimaryConfirm(true);
        return;
      }
    }
    // No conflict, proceed with save
    handleSaveContact();
  };

  const handleConfirmPrimaryChange = () => {
    setShowPrimaryConfirm(false);
    setCurrentPrimaryContact(null);
    // Proceed with save - backend will handle removing PRIMARY from other contact
    handleSaveContact();
  };

  const handleSaveContact = async () => {
    if (editingContact) {
      // Update existing contact
      const input: UpdateContactInput = {
        firstName: formFirstName,
        lastName: formLastName,
        email: formEmail,
        phone: formPhone || undefined,
        title: formTitle || undefined,
        types: formTypes,
      };

      await updateContact({
        variables: { id: editingContact.id, input },
      });
    } else {
      // Create new contact
      const input: CreateContactInput = {
        organizationId: clientId,
        firstName: formFirstName,
        lastName: formLastName,
        email: formEmail,
        phone: formPhone || undefined,
        title: formTitle || undefined,
        types: formTypes,
      };

      await createContact({ variables: { input } });
    }
  };

  const handleDeleteContact = async (contactId: string) => {
    await deleteContact({ variables: { id: contactId } });
  };

  const isLoading = creating || updating || deleting;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate dark:text-mist">Contacts</h2>
          <p className="text-sm text-chalkboard dark:text-rain-cloud">
            {contacts.length === 0 
              ? 'No contacts yet'
              : `${contacts.length} contact${contacts.length !== 1 ? 's' : ''}`}
          </p>
        </div>
        <button onClick={openAddModal} className="btn-primary">
          <Plus className="w-4 h-4" />
          Add Contact
        </button>
      </div>

      {/* Contacts Table */}
      <div className="card overflow-hidden">
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Title</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Type</th>
              <th className="w-24">Actions</th>
            </tr>
          </thead>
          <tbody>
            {contacts.map((contact) => (
              <tr key={contact.id} className="group">
                <td>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-pond/10 dark:bg-pond-light/20 flex items-center justify-center">
                      <User className="w-4 h-4 text-pond dark:text-pond-light" />
                    </div>
                    <span className="font-medium text-slate dark:text-mist">
                      {contact.firstName} {contact.lastName}
                    </span>
                  </div>
                </td>
                <td className="text-chalkboard dark:text-rain-cloud">
                  {contact.title || '—'}
                </td>
                <td>
                  <a 
                    href={`mailto:${contact.email}`} 
                    className="text-pond dark:text-pond-light hover:underline flex items-center gap-1"
                  >
                    <Mail className="w-3 h-3" />
                    {contact.email}
                  </a>
                </td>
                <td>
                  {contact.phone ? (
                    <a 
                      href={`tel:${contact.phone}`} 
                      className="text-slate dark:text-mist hover:text-pond dark:hover:text-pond-light flex items-center gap-1"
                    >
                      <Phone className="w-3 h-3" />
                      {contact.phone}
                    </a>
                  ) : (
                    <span className="text-chalkboard dark:text-rain-cloud">—</span>
                  )}
                </td>
                <td>
                  <div className="flex flex-wrap gap-1">
                    {(contact.types || [contact.type]).map((t) => (
                      <span key={t} className={cn('badge text-xs', CONTACT_TYPE_STYLES[t])}>
                        {CONTACT_TYPE_LABELS[t]}
                      </span>
                    ))}
                  </div>
                </td>
                <td>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button 
                      onClick={() => openEditModal(contact)}
                      disabled={isLoading}
                      className="btn-ghost btn-icon btn-sm"
                      title="Edit contact"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => setShowDeleteConfirm(contact.id)}
                      disabled={isLoading}
                      className="btn-ghost btn-icon btn-sm text-rust dark:text-salmon"
                      title="Delete contact"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {contacts.length === 0 && (
          <div className="p-8 text-center text-chalkboard dark:text-rain-cloud">
            No contacts added yet. Click &quot;Add Contact&quot; to get started.
          </div>
        )}
      </div>

      {/* Contact Types Legend */}
      <div className="card">
        <div className="card-body">
          <p className="text-sm font-medium text-slate dark:text-mist mb-3">Contact Types</p>
          <div className="flex flex-wrap gap-3">
            {Object.entries(CONTACT_TYPE_LABELS).map(([type, label]) => (
              <span key={type} className={cn('badge', CONTACT_TYPE_STYLES[type as ContactType])}>
                {label}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Add/Edit Contact Modal */}
      <Modal 
        isOpen={showAddModal || !!editingContact} 
        onClose={() => { setShowAddModal(false); setEditingContact(null); resetForm(); }}
        title={editingContact ? 'Edit Contact' : 'Add Contact'}
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
                First Name
              </label>
              <input
                type="text"
                value={formFirstName}
                onChange={(e) => setFormFirstName(e.target.value)}
                placeholder="Enter first name"
                className="input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
                Last Name
              </label>
              <input
                type="text"
                value={formLastName}
                onChange={(e) => setFormLastName(e.target.value)}
                placeholder="Enter last name"
                className="input"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
              Job Title
            </label>
            <input
              type="text"
              value={formTitle}
              onChange={(e) => setFormTitle(e.target.value)}
              placeholder="Enter job title"
              className="input"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
                Email Address <span className="text-rust">*</span>
              </label>
              <input
                type="email"
                value={formEmail}
                onChange={(e) => handleEmailChange(e.target.value)}
                onBlur={handleEmailBlur}
                placeholder="email@company.com"
                className={cn('input', emailError && touched.email && 'border-rust dark:border-salmon focus:ring-rust')}
              />
              {emailError && touched.email && (
                <p className="mt-1 text-xs text-rust dark:text-salmon flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {emailError}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
                Phone Number <span className="text-chalkboard dark:text-rain-cloud text-xs font-normal">(optional)</span>
              </label>
              <input
                type="tel"
                value={formPhone}
                onChange={(e) => handlePhoneChange(e.target.value)}
                onBlur={handlePhoneBlur}
                placeholder="(555) 123-4567"
                className={cn('input', phoneError && touched.phone && 'border-rust dark:border-salmon focus:ring-rust')}
              />
              {phoneError && touched.phone && (
                <p className="mt-1 text-xs text-rust dark:text-salmon flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {phoneError}
                </p>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate dark:text-mist mb-2">
              Contact Roles <span className="text-xs text-chalkboard dark:text-rain-cloud font-normal">(select all that apply)</span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(CONTACT_TYPE_LABELS).map(([type, label]) => (
                <label
                  key={type}
                  className={cn(
                    'flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors',
                    formTypes.includes(type as ContactType)
                      ? 'border-pond bg-pond/5 dark:border-pond-light dark:bg-pond-light/10'
                      : 'border-gray dark:border-night-pond hover:border-french-gray dark:hover:border-chalkboard'
                  )}
                >
                  <input
                    type="checkbox"
                    checked={formTypes.includes(type as ContactType)}
                    onChange={() => handleTypeToggle(type as ContactType)}
                    className="w-4 h-4 rounded border-gray dark:border-night-pond text-pond dark:text-pond-light focus:ring-pond"
                  />
                  <span className={cn(
                    'text-sm',
                    formTypes.includes(type as ContactType)
                      ? 'text-slate dark:text-mist font-medium'
                      : 'text-chalkboard dark:text-rain-cloud'
                  )}>
                    {label}
                  </span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button 
              onClick={() => { setShowAddModal(false); setEditingContact(null); resetForm(); }}
              className="btn-secondary"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button 
              onClick={handleSaveClick}
              className="btn-primary"
              disabled={!isFormValid() || isLoading}
            >
              {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
              {editingContact ? 'Save Changes' : 'Add Contact'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(null)}
        title="Delete Contact"
      >
        <div className="space-y-4">
          <p className="text-slate dark:text-mist">
            Are you sure you want to delete this contact? This action cannot be undone.
          </p>
          <div className="flex justify-end gap-3">
            <button 
              onClick={() => setShowDeleteConfirm(null)} 
              className="btn-secondary"
              disabled={deleting}
            >
              Cancel
            </button>
            <button 
              onClick={() => showDeleteConfirm && handleDeleteContact(showDeleteConfirm)}
              className="btn-primary bg-rust hover:bg-rust/90"
              disabled={deleting}
            >
              {deleting && <Loader2 className="w-4 h-4 animate-spin" />}
              <Trash2 className="w-4 h-4" />
              Delete
            </button>
          </div>
        </div>
      </Modal>

      {/* Primary Contact Confirmation Modal */}
      <Modal
        isOpen={showPrimaryConfirm}
        onClose={() => {
          setShowPrimaryConfirm(false);
          setCurrentPrimaryContact(null);
        }}
        title="Change Primary Contact"
      >
        <div className="space-y-4">
          <p className="text-slate dark:text-mist">
            <strong className="font-semibold">{currentPrimaryContact?.firstName} {currentPrimaryContact?.lastName}</strong> is currently the primary contact.
          </p>
          <p className="text-slate dark:text-mist">
            Do you want to make <strong className="font-semibold">{formFirstName} {formLastName}</strong> the new primary contact?
          </p>
          <p className="text-sm text-chalkboard dark:text-rain-cloud">
            The primary designation will be removed from the current primary contact.
          </p>
          <div className="flex justify-end gap-3 pt-2">
            <button 
              onClick={() => {
                setShowPrimaryConfirm(false);
                setCurrentPrimaryContact(null);
              }} 
              className="btn-secondary"
            >
              Cancel
            </button>
            <button 
              onClick={handleConfirmPrimaryChange}
              className="btn-primary"
            >
              Change Primary Contact
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
